import React, { useState } from "react";
import { Button, TextField, Paper } from "@mui/material";
import axios from "axios";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import logo from "../assets/digifab-logo-siyah.png";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { API_URL } from "../config";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockResetIcon from "@mui/icons-material/LockReset";

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {"Copyright © "}
      <Link color="inherit" href="#" underline="hover">
        DigiFab
      </Link>{" "}
      {new Date().getFullYear()}
    </Typography>
  );
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#6366f1',
    },
    secondary: {
      main: '#8b5cf6',
    },
  },
});

const Forgot = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleCheckUser = async () => {
    const normalizedEmail = (email || "").trim().toLowerCase();

    if (!normalizedEmail) {
      toast.error("Lütfen e-posta adresinizi girin.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      toast.error("Lütfen geçerli bir e-posta adresi girin.");
      return;
    }

    try {
      setSubmitting(true);

      const response = await axios.post(
        `${API_URL}/auth/request-password-reset`,
        { email: normalizedEmail },
        { headers: { "Content-Type": "application/json" } }
      );

      console.log("Response:", response.data);
      toast.success("Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.");
    } catch (error) {
      console.error("Hata:", error);

      const detail =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Bir hata oluştu.";

      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: 2,
        }}
      >
        <CssBaseline />
        <Container component="main" maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              padding: { xs: 3, sm: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src={logo}
              alt="DigiFab Logo"
              sx={{
                width: '60%',
                maxWidth: '250px',
                height: 'auto',
                marginBottom: 2,
              }}
            />

            {/* Icon & Title */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 2,
              }}
            >
              <LockResetIcon sx={{ fontSize: 32, color: '#fff' }} />
            </Box>

            <Typography
              component="h1"
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                marginBottom: 1,
                textAlign: 'center',
              }}
            >
              Şifre Sıfırlama
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#6b7280',
                marginBottom: 3,
                textAlign: 'center',
                px: 2,
              }}
            >
              Sisteme kayıtlı e-posta adresinizi girin. Size şifre sıfırlama bağlantısı gönderilecektir.
            </Typography>

            {/* Form */}
            <Box noValidate sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="E-posta Adresi"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCheckUser();
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <Button
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.4)',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.6)',
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  },
                }}
                onClick={handleCheckUser}
                disabled={submitting}
              >
                {submitting ? "Gönderiliyor..." : "Sıfırlama Bağlantısı Gönder"}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                sx={{
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '15px',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    backgroundColor: 'rgba(99, 102, 241, 0.04)',
                  },
                }}
                onClick={() => navigate('/')}
              >
                Giriş Sayfasına Dön
              </Button>
            </Box>

            <Copyright sx={{ mt: 3 }} />
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Forgot;